import { FC, ReactNode } from "react";
import { Trans } from "react-i18next";
import { TOptions } from "i18next";
import { useAppSelector } from "store/hooks";
import {
  bankCardsSelector,
  companiesListSelector,
  companyMetadataSelector,
  rewardPointsSelector,
  userMetadataSelector,
} from "store/selectors";

import { UserRole } from "types/BETypes";
import { hasEmployeeRights } from "permissions/helpers/shared";
import { hasAccessToInternalBankAccount } from "helpers/bankAccount/internalBankAccount";
import useRouteProperties from "hooks/useRouteProperties";
import AccountMenu from "components/AccountMenu";
import { AiAssistantModal } from "components/AiAssistantComponents";
import BackButton from "components/BackButton";
import Breadcrumbs from "components/Breadcrumbs";
import { CompanySwitcherDropdown } from "components/CompanySwitcherDropdown";
import MainFooter from "components/MainFooter";
import MainMenu from "components/MainMenu";
import PermissionContent from "components/PermissionContent";
import RewardPointsDisplay from "components/RewardPointsDisplay";

import {
  ContentContainer,
  PageTitle,
  PageTitleRightContent,
  PageTitleWrapperMargined,
} from "uikit";

import {
  BreadcrumbsContainer,
  Container,
  Content,
  ContentBlock,
  ContentBlockInner,
  TCContent,
  TCInner,
  TCInnerLeft,
  TCInnerRight,
  TopContent,
  TopContentWrapper,
  TopMain,
} from "./styles";

interface WrapperProps extends Props {
  backBtnText?: string;
  onBackBtnClick?: () => void;
}

interface Props {
  className?: string;
  title?: ReactNode | string;
  showTitle?: boolean;
  titleRightContent?: ReactNode;
  showBreadcrumbs?: boolean;
  metaDataLoading?: boolean;
  translationValues?: TOptions;
  children?: ReactNode | string;
  topContent?: ReactNode | string;
  mobileTopContent?: ReactNode | string;
  innerTopContent?: ReactNode | string;
  contentBg?: string;
  showUserMenu?: boolean;
  showResponsiveMenu?: boolean;
  needRenderMenuForResponsive?: boolean;
  responsivePageName?: ReactNode | string;
}

const DashboardLayout: FC<Props> = ({
  className,
  title,
  showTitle = true,
  titleRightContent,
  showBreadcrumbs = true,
  metaDataLoading = false,
  translationValues,

  children,
  topContent,
  mobileTopContent,
  innerTopContent,
  contentBg,
  showUserMenu = true,
  showResponsiveMenu = true,
  needRenderMenuForResponsive = false,
  responsivePageName,
}) => {
  const currentUser = useAppSelector(userMetadataSelector);
  const currentCompaniesList = useAppSelector(companiesListSelector);
  const currentCompany = useAppSelector(companyMetadataSelector);
  const currentBankCards = useAppSelector(bankCardsSelector);
  const rewardPoints = useAppSelector(rewardPointsSelector);
  const { getCurrentRouteConfig, getBreadcrumbsChainForCurrentRoute } = useRouteProperties();
  const currentRouteConfig = getCurrentRouteConfig();
  const breadcrumbs = getBreadcrumbsChainForCurrentRoute(translationValues);
  const userIsEmployee = hasEmployeeRights(currentUser);

  const shouldShowInternalBankAccountSection = hasAccessToInternalBankAccount(
    currentUser,
    currentCompany,
    currentBankCards,
  );
  const shouldShowRewardPoints =
    shouldShowInternalBankAccountSection &&
    userIsEmployee &&
    currentUser?.activeCompanyUser?.isOnboardingComplete;

  const roleClassName = `role-${currentUser?.activeCompanyUser?.lastActiveRole}`;
  const shouldShowCompanySwitcher = (currentCompaniesList?.total ?? 0) > 1;

  const pageTitle =
    title || currentRouteConfig?.title ? (
      <Trans i18nKey={currentRouteConfig?.title} values={translationValues} />
    ) : (
      ""
    );

  const renderBreadcrumbs = () => {
    const shouldShowBreadcrumbs =
      !!showBreadcrumbs && !metaDataLoading && !!breadcrumbs?.length && breadcrumbs.length > 1;
    return (
      <>
        {!!shouldShowBreadcrumbs && (
          <BreadcrumbsContainer>
            <Breadcrumbs breadcrumbs={breadcrumbs} />
          </BreadcrumbsContainer>
        )}
      </>
    );
  };

  const renderPageTitle = () => {
    const shouldShowTitle = (!!showTitle || titleRightContent) && !metaDataLoading;
    return (
      <>
        {!!shouldShowTitle && (
          <PageTitleWrapperMargined>
            <PageTitle>{pageTitle}</PageTitle>
            {titleRightContent && (
              <PageTitleRightContent>{titleRightContent}</PageTitleRightContent>
            )}
          </PageTitleWrapperMargined>
        )}
      </>
    );
  };

  return (
    <Container className={`${className} ${roleClassName}`}>
      <TopMain>
        <MainMenu needRenderMenuForResponsive={needRenderMenuForResponsive} />
        <ContentBlock style={{ backgroundImage: contentBg ? `url(${contentBg})` : "none" }}>
          <ContentBlockInner>
            <TopContentWrapper>
              <TopContent>
                <TCInner>
                  <TCInnerLeft>
                    {topContent && <TCContent>{topContent}</TCContent>}
                    {shouldShowCompanySwitcher && <CompanySwitcherDropdown />}
                  </TCInnerLeft>
                  <TCInnerRight>
                    <PermissionContent
                      roles={[UserRole.EMPLOYEE]}
                      elseContent={<AiAssistantModal />}
                    />

                    {shouldShowRewardPoints && (
                      <RewardPointsDisplay amount={rewardPoints?.pointsAmount || 0} />
                    )}
                    {showUserMenu && <AccountMenu />}
                  </TCInnerRight>
                </TCInner>
              </TopContent>
            </TopContentWrapper>
            {innerTopContent}
            <Content>
              <ContentContainer>
                {renderBreadcrumbs()}
                {renderPageTitle()}

                {children}
              </ContentContainer>
              <MainFooter />
            </Content>
          </ContentBlockInner>
        </ContentBlock>
      </TopMain>
    </Container>
  );
};

const DashboardLayoutWrapper: FC<WrapperProps> = ({ onBackBtnClick, backBtnText, ...rest }) => {
  const renderBackButton = () => (
    <BackButton
      onClick={() => (onBackBtnClick ? onBackBtnClick() : undefined)}
      data-testid="back-button"
    >
      <Trans i18nKey={backBtnText || "common.back"} />
    </BackButton>
  );
  return (
    <DashboardLayout
      {...rest}
      topContent={onBackBtnClick && renderBackButton()}
      /* TODO:::      mobileTopContent={onBackBtnClick && renderBackButton()} //According to new desings, we won't have a back button on top on mobile devices */
    />
  );
};

export default DashboardLayoutWrapper;
